import { computed, onBeforeUnmount, ref, toValue } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";
import { useUserStore } from "~/stores/user";
import { cleanParams, mapTrackingLanguage } from "~/composables/useSiteComponentTracking";

export function getContentDuration(info) {
	if (info?.media_second_str) return info.media_second_str;
	const seconds = Number(info?.media_second);
	if (Number.isFinite(seconds) && seconds > 0) return String(seconds);
	return undefined;
}

export const DETAIL_MEDIA_TYPES = {
	PODCAST: "podcast",
	SHORT: "short",
	ARTICLE: "article",
};

const PROGRESS_MILESTONES = [25, 50, 75, 100];

const PAGE_VIEW_EVENTS = {
	[DETAIL_MEDIA_TYPES.PODCAST]: "podcast_detail_page_view",
	[DETAIL_MEDIA_TYPES.SHORT]: "short_detail_page_view",
	[DETAIL_MEDIA_TYPES.ARTICLE]: "article_detail_page_view",
};

const PLAY_PROGRESS_EVENTS = {
	[DETAIL_MEDIA_TYPES.PODCAST]: "podcast_play_progress",
	[DETAIL_MEDIA_TYPES.SHORT]: "short_play_progress",
};

function getSessionId() {
	if (!import.meta.client) return "";
	try {
		const key = "seer_media_square_session_id";
		const cached = sessionStorage.getItem(key);
		if (cached) return cached;
		const id =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		sessionStorage.setItem(key, id);
		return id;
	} catch {
		return "";
	}
}

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";
	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

export function resolveDetailMediaType(squareInfo) {
	if (Number(squareInfo?.video_screen_mode) === 2) return DETAIL_MEDIA_TYPES.SHORT;
	if (Number(squareInfo?.type) === 2) return DETAIL_MEDIA_TYPES.ARTICLE;
	return DETAIL_MEDIA_TYPES.PODCAST;
}

export function getSquareContentId(squareInfo) {
	return squareInfo?.id != null ? String(squareInfo.id) : undefined;
}

export function getSquareAuthorId(squareInfo) {
	const uid = squareInfo?.uid || squareInfo?.publisher_info?.uid;
	return uid != null ? String(uid) : undefined;
}

export function bucketWatchTime(seconds) {
	const value = Number(seconds) || 0;
	if (value <= 5) return "0-5s";
	if (value <= 30) return "6-30s";
	if (value <= 120) return "31-120s";
	return "120s+";
}

export function bucketReadTime(seconds) {
	const value = Number(seconds) || 0;
	if (value <= 10) return "0-10s";
	if (value <= 60) return "11-60s";
	if (value <= 180) return "61-180s";
	return "180s+";
}

export function bucketCommentLength(length) {
	const value = Number(length) || 0;
	if (value <= 10) return "0-10";
	if (value <= 50) return "11-50";
	if (value <= 100) return "51-100";
	return "100+";
}

export function bucketCommentCount(count) {
	const value = Number(count) || 0;
	if (value <= 0) return "0";
	if (value <= 10) return "1-10";
	if (value <= 50) return "11-50";
	if (value <= 100) return "51-100";
	return "100+";
}

export function bucketArticleLength(content) {
	const text = String(content || "")
		.replace(/<[^>]+>/g, "")
		.trim();
	const len = text.length;
	if (len <= 500) return "0-500";
	if (len <= 1500) return "501-1500";
	if (len <= 3000) return "1501-3000";
	return "3000+";
}

export function hashCommentId(commentId) {
	if (commentId == null) return undefined;
	const raw = String(commentId);
	let hash = 0;
	for (let i = 0; i < raw.length; i += 1) {
		hash = (hash << 5) - hash + raw.charCodeAt(i);
		hash |= 0;
	}
	return `c_${Math.abs(hash)}`;
}

export function mapShareChannel(platformText) {
	if (!platformText) return "unknown";
	return String(platformText).trim().toLowerCase().replace(/\s+/g, "_");
}

export function buildDetailContentParams(squareInfo, extra = {}) {
	const info = squareInfo || {};
	return cleanParams({
		content_id: getSquareContentId(info),
		author_id: getSquareAuthorId(info),
		duration: getContentDuration(info),
		media_type: resolveDetailMediaType(info),
		...extra,
	});
}

export function buildRelatedItemParams(currentInfo, relatedItem, position) {
	return buildDetailContentParams(currentInfo, {
		related_content_id: getSquareContentId(relatedItem),
		related_media_type: resolveDetailMediaType(relatedItem),
		position,
	});
}

export function buildHotContentParams(currentInfo, hotItem, { rank, position } = {}) {
	return buildDetailContentParams(currentInfo, {
		hot_content_id: getSquareContentId(hotItem),
		rank,
		position,
	});
}

export function buildAuthorContactParams(squareInfo, mentorId) {
	return buildDetailContentParams(squareInfo, {
		mentor_id: mentorId != null ? String(mentorId) : undefined,
		entry_source: getEntrySource(),
	});
}

/**
 * 媒体广场详情页埋点。调用方式：logDetailEvent('podcast_play_click', { ... })
 */
export function useSquareDetailTracking(deviceType = "pc", getSquareInfo) {
	const { locale } = useI18n();
	const { isLogin } = storeToRefs(useUserStore());
	const viewedContentIds = new Set();

	const baseParams = computed(() =>
		cleanParams({
			session_id: getSessionId(),
			login_status: isLogin.value ? "logged_in" : "guest",
			language: mapTrackingLanguage(locale?.value || "zh-TW"),
			device_type: deviceType,
			entry_source: getEntrySource(),
		})
	);

	const getSquareInfoValue = () => toValue(getSquareInfo) || {};

	const getMediaType = () => resolveDetailMediaType(getSquareInfoValue());

	function logDetailEvent(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	function trackDetailPageView() {
		const info = getSquareInfoValue();
		const contentId = getSquareContentId(info);
		if (!contentId || viewedContentIds.has(contentId)) return;
		viewedContentIds.add(contentId);

		const mediaType = resolveDetailMediaType(info);
		const eventName = PAGE_VIEW_EVENTS[mediaType];
		if (!eventName) return;

		const extra =
			mediaType === DETAIL_MEDIA_TYPES.ARTICLE
				? {
						article_length_bucket: bucketArticleLength(
							info.content || info.content_en || info.desc
						),
					}
				: {};

		logDetailEvent(eventName, buildDetailContentParams(info, extra));
	}

	function trackAuthorFollowClick({ followStatusBefore } = {}) {
		const info = getSquareInfoValue();
		const mediaType = getMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT
				? "short_author_follow_click"
				: "media_author_follow_click";

		logDetailEvent(
			eventName,
			buildDetailContentParams(info, {
				media_type: mediaType,
				follow_status_before: followStatusBefore ? "followed" : "unfollowed",
			})
		);
	}

	function trackLikeClick({ likeStatusBefore } = {}) {
		const info = getSquareInfoValue();
		const mediaType = getMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT ? "short_like_click" : "media_like_click";

		logDetailEvent(
			eventName,
			buildDetailContentParams(info, {
				media_type: mediaType,
				like_status_before: likeStatusBefore ? "liked" : "unliked",
			})
		);
	}

	function trackShareClick({ shareChannel } = {}) {
		const info = getSquareInfoValue();
		const mediaType = getMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT ? "short_share_click" : "media_share_click";

		logDetailEvent(
			eventName,
			buildDetailContentParams(info, {
				media_type: mediaType,
				share_channel: mapShareChannel(shareChannel),
			})
		);
	}

	return {
		logDetailEvent,
		trackDetailPageView,
		trackAuthorFollowClick,
		trackLikeClick,
		trackShareClick,
		getMediaType,
		buildDetailContentParams: extra => buildDetailContentParams(getSquareInfoValue(), extra),
	};
}

/** 播放进度 25/50/75/100 */
export function usePlayProgressTracking(logDetailEvent, getSquareInfo) {
	const firedMilestones = new Set();
	let watchStartedAt = 0;

	const reset = () => {
		firedMilestones.clear();
		watchStartedAt = 0;
	};

	const trackPlayStart = ({ autoplay = true } = {}) => {
		if (!logDetailEvent) return;
		const info = toValue(getSquareInfo) || {};
		if (getMediaTypeFromInfo(info) !== DETAIL_MEDIA_TYPES.SHORT) return;
		watchStartedAt = Date.now();
		logDetailEvent(
			"short_play_start",
			buildDetailContentParams(info, {
				autoplay,
			})
		);
	};

	const trackPlayClick = ({ playPositionBucket = "main" } = {}) => {
		if (!logDetailEvent) return;
		const info = toValue(getSquareInfo) || {};
		if (getMediaTypeFromInfo(info) !== DETAIL_MEDIA_TYPES.PODCAST) return;
		logDetailEvent(
			"podcast_play_click",
			buildDetailContentParams(info, {
				play_position_bucket: playPositionBucket,
			})
		);
	};

	const handleTimeUpdate = videoEl => {
		if (!logDetailEvent) return;
		const info = toValue(getSquareInfo) || {};
		const mediaType = getMediaTypeFromInfo(info);
		const eventName = PLAY_PROGRESS_EVENTS[mediaType];
		if (!eventName || !videoEl?.duration) return;

		const percent = Math.floor((videoEl.currentTime / videoEl.duration) * 100);
		const watchSeconds =
			watchStartedAt > 0 ? Math.floor((Date.now() - watchStartedAt) / 1000) : videoEl.currentTime;

		PROGRESS_MILESTONES.forEach(milestone => {
			const key = `${getSquareContentId(info)}_${milestone}`;
			if (percent < milestone || firedMilestones.has(key)) return;
			firedMilestones.add(key);
			logDetailEvent(
				eventName,
				buildDetailContentParams(info, {
					progress_percent: milestone,
					watch_time_bucket: bucketWatchTime(watchSeconds),
				})
			);
		});
	};

	const markWatchStart = () => {
		if (!watchStartedAt) watchStartedAt = Date.now();
	};

	onBeforeUnmount(reset);

	return {
		reset,
		trackPlayStart,
		trackPlayClick,
		handleTimeUpdate,
		markWatchStart,
	};
}

function getMediaTypeFromInfo(info) {
	return resolveDetailMediaType(info);
}

/** 图文阅读滚动进度 */
export function useArticleReadProgress(logDetailEvent, getSquareInfo, scrollTargetRef) {
	const firedMilestones = new Set();
	let readStartedAt = 0;

	const handleScroll = () => {
		if (!logDetailEvent) return;
		const el = toValue(scrollTargetRef);
		const info = toValue(getSquareInfo) || {};
		if (!el || getMediaTypeFromInfo(info) !== DETAIL_MEDIA_TYPES.ARTICLE) return;

		if (!readStartedAt) readStartedAt = Date.now();

		const scrollTop = el.scrollTop || window.scrollY;
		const scrollHeight = el.scrollHeight - el.clientHeight;
		if (scrollHeight <= 0) return;

		const percent = Math.floor((scrollTop / scrollHeight) * 100);
		const readSeconds = Math.floor((Date.now() - readStartedAt) / 1000);

		PROGRESS_MILESTONES.forEach(milestone => {
			const key = `${getSquareContentId(info)}_${milestone}`;
			if (percent < milestone || firedMilestones.has(key)) return;
			firedMilestones.add(key);
			logDetailEvent(
				"article_read_progress",
				buildDetailContentParams(info, {
					progress_percent: milestone,
					read_time_bucket: bucketReadTime(readSeconds),
				})
			);
		});
	};

	return { handleScroll };
}

/** 推荐/热门列表曝光 */
export function useDetailListImpression({ logDetailEvent, impressionEvent, getItemParams }) {
	const impressedKeys = new Set();
	const observerStops = new Map();

	onBeforeUnmount(() => {
		observerStops.forEach(stop => stop());
		observerStops.clear();
	});

	function bindItemRef(el, item, index) {
		if (!logDetailEvent) return;
		const relatedId = item?.id;
		if (!relatedId) return;

		const impressionKey = String(relatedId);
		if (!el) {
			observerStops.get(impressionKey)?.();
			observerStops.delete(impressionKey);
			return;
		}
		if (impressedKeys.has(impressionKey) || observerStops.has(impressionKey)) return;

		const targetRef = ref(el);
		let dwellTimer = null;
		const clearDwellTimer = () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				dwellTimer = null;
			}
		};

		const { stop } = useIntersectionObserver(
			targetRef,
			([entry]) => {
				if (impressedKeys.has(impressionKey)) return;
				if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
					clearDwellTimer();
					dwellTimer = setTimeout(() => {
						if (impressedKeys.has(impressionKey)) return;
						impressedKeys.add(impressionKey);
						const extra = typeof getItemParams === "function" ? getItemParams(item, index) : {};
						logDetailEvent(impressionEvent, cleanParams(extra));
						stop();
						observerStops.delete(impressionKey);
					}, 1000);
					return;
				}
				clearDwellTimer();
			},
			{ threshold: [0, 0.5, 1] }
		);

		observerStops.set(impressionKey, () => {
			clearDwellTimer();
			stop();
		});
	}

	return { bindItemRef };
}
